import { Box, HStack, Text, VStack } from '@chakra-ui/react'
import { flow } from '@helo/utils'
import type { ReactNode } from 'react'

import { useChartColors } from '../../../components/charts/common/useChartColors'
import type { QueryOutput } from '../../Query'
import { RenderPipeline } from '../../RenderPipeline'
import { ChartBarConstants } from '../ChartBar/ChartBar.constants'
import type { ChartPieOutput, ResolvedChartPieSlice } from '../ChartPie'
import { ChartPieConstants } from '../ChartPie/ChartPie.constants'
import type { ChartSeriesOutput, ResolvedChartSeries } from '../ChartSeries'
import { LineSeriesMarker } from '../LineSeriesMarker'
import { ChartLegendStyles } from './ChartLegend.constants'
import {
  type ChartLegendOptions,
  type ChartLegendOrientation,
  type ChartLegendPosition,
  chartLegendOptions,
} from './ChartLegend.options'
import {
  ChartLegendInputParts,
  ChartLegendOutputParts,
} from './ChartLegend.parts'

export type {
  ChartLegendOrientation,
  ChartLegendPosition,
} from './ChartLegend.options'
export { chartLegendOptions } from './ChartLegend.options'
export type { ChartLegendOptions } from './ChartLegend.options'

/** Pipeline input consumed by ChartLegend.setup. */
export type ChartLegendInput = ChartLegendOptions

export type ChartLegendSlice = {
  visible: boolean
  orientation: ChartLegendOrientation
  position: ChartLegendPosition
}

/** Pipeline output slice — namespaced legend config. */
export type ChartLegendOutput = {
  legend: ChartLegendSlice
}

export type ChartLegendPriorOutput = Pick<QueryOutput, 'contentNode'>

const chartLegendSetup = () =>
  RenderPipeline.createFeatureSetup<
    ChartLegendInput,
    ChartLegendOutput,
    ChartLegendPriorOutput
  >(
    flow(
      ChartLegendInputParts.setupInput(),
      ChartLegendInputParts.setup_legendInput(),
      ChartLegendOutputParts.setLegend(),
    ),
  )

export interface ChartLegendProps {
  renderConfig: ChartLegendOutput & ChartSeriesOutput & Partial<ChartPieOutput>
}

type LegendSeriesItem = ResolvedChartSeries & {
  color?: string
  fillColor?: string
  pointRadius?: number
  lineWidth?: number
}

interface BarSeriesMarkerProps {
  color: string
}

const BarSeriesMarker = ({ color }: BarSeriesMarkerProps) => (
  <Box
    as="span"
    role="img"
    aria-hidden
    flexShrink={0}
    w={`${ChartBarConstants.legendSwatchSizePx}px`}
    h={`${ChartBarConstants.legendSwatchSizePx}px`}
    bg={color}
  />
)

interface PieSliceMarkerProps {
  color: string
}

const PieSliceMarker = ({ color }: PieSliceMarkerProps) => (
  <Box
    as="span"
    role="img"
    aria-hidden
    flexShrink={0}
    w={`${ChartPieConstants.legendDotSizePx}px`}
    h={`${ChartPieConstants.legendDotSizePx}px`}
    borderRadius="full"
    bg={color}
    border={ChartPieConstants.legendDotBorder}
    boxShadow={ChartPieConstants.legendDotShadow}
    data-testid="pie-chart-legend-dot"
  />
)

interface PieLegendItemProps {
  slice: ResolvedChartPieSlice
}

const PieLegendItem = ({ slice }: PieLegendItemProps) => {
  const chartColors = useChartColors()

  return (
    <HStack gap={ChartLegendStyles.item.gap} alignItems="center">
      <PieSliceMarker color={slice.color} />
      <Text
        as="span"
        color={chartColors.pieLegendLabelColor}
        fontSize={ChartLegendStyles.label.fontSize}
        fontWeight={ChartLegendStyles.label.fontWeight}
        fontFamily={ChartLegendStyles.label.fontFamily}
        lineHeight={ChartLegendStyles.label.lineHeight}
      >
        {slice.label}
      </Text>
    </HStack>
  )
}

interface SeriesLegendItemProps {
  seriesItem: LegendSeriesItem
}

const SeriesLegendItem = ({ seriesItem }: SeriesLegendItemProps) => {
  const chartColors = useChartColors()

  return (
    <HStack gap={ChartLegendStyles.item.gap} alignItems="center">
      {seriesItem.visualization === 'bar' ? (
        <BarSeriesMarker color={seriesItem.color ?? ''} />
      ) : (
        <LineSeriesMarker
          color={seriesItem.color ?? ''}
          fillColor={seriesItem.fillColor ?? seriesItem.color ?? ''}
          pointRadius={seriesItem.pointRadius}
          lineWidth={seriesItem.lineWidth}
          maskId={`chart-legend-point-gap-${seriesItem.seriesKey}`}
        />
      )}
      <Text
        as="span"
        color={chartColors.legendLabelColor}
        fontSize={ChartLegendStyles.label.fontSize}
        fontWeight={ChartLegendStyles.label.fontWeight}
        fontFamily={ChartLegendStyles.label.fontFamily}
        lineHeight={ChartLegendStyles.label.lineHeight}
      >
        {seriesItem.label ?? seriesItem.seriesKey}
      </Text>
    </HStack>
  )
}

const Legend = ({ renderConfig }: ChartLegendProps): ReactNode => {
  const { legend, series, pie } = renderConfig
  if (legend == null) return null
  const { visible, orientation } = legend
  if (!visible) return null

  const items =
    pie?.slices != null && pie.slices.length > 0
      ? pie.slices.map(slice => <PieLegendItem key={slice.key} slice={slice} />)
      : series == null || series.length === 0
        ? []
        : series.map(seriesItem => (
            <SeriesLegendItem
              key={seriesItem.seriesKey}
              seriesItem={seriesItem as LegendSeriesItem}
            />
          ))

  if (items.length === 0) return null

  if (orientation === 'horizontal') {
    return (
      <HStack
        gap={ChartLegendStyles.stack.gap}
        flexWrap="wrap"
        alignItems="center"
        alignSelf="flex-end"
        py={ChartLegendStyles.stack.py}
      >
        {items}
      </HStack>
    )
  }

  return (
    <VStack
      gap={ChartLegendStyles.stack.gap}
      alignItems="flex-start"
      alignSelf="flex-end"
      py={ChartLegendStyles.stack.py}
    >
      {items}
    </VStack>
  )
}

export const ChartLegend = {
  options: chartLegendOptions,
  setup: chartLegendSetup,
  Legend,
}
